import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Navbar from '../../components/common/Navbar';
import AddressInput from '../../components/common/AddressInput';
import { orderTitle } from '../../utils/orderTitle';
import { placeName } from '../../utils/orderCardFormat';
import { statusInfo as getStatusInfo } from '../../utils/orderStatus';
import { COLORS, FONTS } from '../../styles/tokens';

const EMPTY_ADDR = { street: '', city: '', lat: null, lng: null };

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAddr,  setEditingAddr]  = useState(false);
  const [draftAddress, setDraftAddress] = useState(EMPTY_ADDR);
  const [savingAddr,   setSavingAddr]   = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'projects', projectId)).then(snap => {
      if (snap.exists()) setProject({ id: snap.id, ...snap.data() });
    });
  }, [projectId]);

  useEffect(() => {
    const q = query(collection(db, 'orders'), where('projectId', '==', projectId));
    return onSnapshot(q, snap => {
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [projectId]);

  async function handleSaveAddress() {
    if (!draftAddress.street.trim()) return;
    setSavingAddr(true);
    try {
      await updateDoc(doc(db, 'projects', projectId), { address: draftAddress });
      setProject(prev => ({ ...prev, address: draftAddress }));
      setEditingAddr(false);
    } finally {
      setSavingAddr(false);
    }
  }

  const invoicedOrders = orders.filter(o => ['invoiced', 'completed'].includes(o.status));
  const totalAmount    = invoicedOrders.reduce((s, o) => s + (o.fixedPrice ?? o.suggestedPrice ?? 0), 0);
  const activeOrders   = orders.filter(o => !['invoiced', 'completed', 'disputed'].includes(o.status));

  return (
    <div className="orderer-ui" style={styles.root}>
      <Navbar />
      <div style={styles.page}>
        <button style={styles.backBtn} onClick={() => navigate('/orderer/projects')} aria-label="Tilbake til prosjekter">
          <svg width="12" height="20" viewBox="0 0 12 20" fill="none" aria-hidden="true"><path d="M10 2L2 10l8 8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>

        {project && (
          <div style={{ marginBottom: 6 }}>
            <div style={styles.mono}>PROSJEKT</div>
            <h1 style={styles.pageTitle}>{project.name}</h1>
            {project.description && <p style={styles.desc}>{project.description}</p>}

            {editingAddr ? (
              <div style={styles.addrEditBox}>
                <AddressInput label="Prosjektadresse" value={draftAddress} onChange={setDraftAddress} />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button style={styles.addrSaveBtn} onClick={handleSaveAddress} disabled={savingAddr || !draftAddress.street.trim()}>
                    {savingAddr ? 'Lagrer…' : 'Lagre adresse'}
                  </button>
                  <button style={styles.addrCancelBtn} onClick={() => setEditingAddr(false)}>Avbryt</button>
                </div>
              </div>
            ) : project.address?.street ? (
              <div style={styles.addrRow}>
                <span style={styles.addrText}>{project.address.street}{project.address.city ? `, ${project.address.city}` : ''}</span>
                <button style={styles.addrEditBtn} onClick={() => { setDraftAddress(project.address); setEditingAddr(true); }}>Endre</button>
              </div>
            ) : (
              <button style={styles.addrEditBtn} onClick={() => { setDraftAddress(EMPTY_ADDR); setEditingAddr(true); }}>
                + Legg til prosjektadresse
              </button>
            )}
          </div>
        )}

        <div style={styles.kpis}>
          <Kpi dark value={orders.length} label="Oppdrag" />
          <Kpi value={activeOrders.length} label="Aktive" />
          <Kpi value={invoicedOrders.length} label="Fullførte" />
          <Kpi value={totalAmount.toLocaleString('nb-NO')} label="Samlet forbruk (kr)" />
        </div>

        <h2 style={styles.h2}>Oppdrag</h2>
        {loading ? (
          <p style={styles.state}>Laster oppdrag…</p>
        ) : orders.length === 0 ? (
          <p style={styles.emptyText}>Ingen oppdrag i dette prosjektet ennå.</p>
        ) : (
          orders.map(order => (
            <OrderRow key={order.id} order={order} onClick={() => navigate(`/orderer/order/${order.id}`)} />
          ))
        )}

        <button className="tippi-btn" style={styles.newOrderBtn} onClick={() => navigate(`/orderer/create?projectId=${projectId}`)}>
          + Legg til oppdrag i prosjektet
        </button>
      </div>
    </div>
  );
}

function Kpi({ value, label, dark }) {
  const long = String(value).length > 3;
  return (
    <div style={{ ...styles.kpi, ...(dark ? styles.kpiDark : {}) }}>
      <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: long ? 21 : 30, lineHeight: 1.1, color: dark ? COLORS.accent : COLORS.textDark }}>{value}</div>
      <div style={{ fontSize: 10.5, lineHeight: 1.2, marginTop: 3, color: dark ? 'rgba(255,255,255,0.7)' : COLORS.textLight }}>{label}</div>
    </div>
  );
}

function OrderRow({ order, onClick }) {
  const st = getStatusInfo(order.status);
  const scheduled = order.scheduledAt?.toDate?.();
  return (
    <button type="button" style={styles.row} onClick={onClick}>
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={styles.rowTitle}>{orderTitle(order)}</div>
        <div style={styles.rowSub}>{placeName(order.pickupAddress)} → {placeName(order.deliveryAddress)}</div>
        {scheduled && (
          <div style={styles.rowMono}>{scheduled.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }).toUpperCase()}</div>
        )}
      </div>
      <div style={styles.rowRight}>
        <span style={{ ...styles.badge, background: st.bg, color: st.color }}>{st.text}</span>
        <span style={styles.rowPrice}>{order.fixedPrice ? order.fixedPrice.toLocaleString('nb-NO') : 'Anbud'}</span>
      </div>
    </button>
  );
}

const styles = {
  root:         { minHeight: '100vh', background: COLORS.bg, display: 'flex', flexDirection: 'column' },
  page:         { maxWidth: 720, margin: '0 auto', padding: '24px 20px', width: '100%', paddingTop: 'max(24px, env(safe-area-inset-top))' },
  backBtn:      { width: 44, height: 44, borderRadius: '50%', border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginBottom: 14 },
  mono:         { fontFamily: 'var(--font-mono)', fontSize: 11, color: COLORS.textLight, letterSpacing: '0.04em' },
  pageTitle:    { fontFamily: FONTS.display, fontSize: 38, lineHeight: 1.02, fontWeight: 700, color: COLORS.textDark, margin: '2px 0 0' },
  desc:         { color: COLORS.textMid, fontSize: 14, margin: '6px 0 0' },
  kpis:         { display: 'flex', gap: 8, margin: '16px 0 8px' },
  kpi:          { flex: 1, minWidth: 0, background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: '10px 10px 9px' },
  kpiDark:      { background: COLORS.brandPanel, border: 'none' },
  h2:           { fontFamily: FONTS.display, fontSize: 22, fontWeight: 700, margin: '14px 0 2px', color: COLORS.textDark },
  newOrderBtn:  { width: '100%', height: 52, borderRadius: 26, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontWeight: 700, fontSize: 16, cursor: 'pointer', marginTop: 20 },
  row:          { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 0', background: 'none', border: 'none', borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer', fontFamily: 'inherit', color: COLORS.textDark },
  rowTitle:     { fontWeight: 700, fontSize: 15 },
  rowSub:       { fontSize: 13, color: COLORS.textMid, marginTop: 1 },
  rowMono:      { fontFamily: 'var(--font-mono)', fontSize: 10.5, color: COLORS.textLight, marginTop: 3 },
  rowRight:     { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 },
  rowPrice:     { fontFamily: FONTS.display, fontWeight: 700, fontSize: 20, lineHeight: 1 },
  badge:        { borderRadius: 20, padding: '4px 10px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },
  emptyText:    { color: COLORS.textMid, fontSize: 14, padding: '14px 0' },
  state:        { textAlign: 'center', color: COLORS.textLight, padding: 40 },
  addrRow:      { display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' },
  addrText:     { fontSize: 13, color: COLORS.textMid },
  addrEditBtn:  { background: 'none', border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: '5px 12px', fontSize: 12.5, fontWeight: 600, color: COLORS.textDark, cursor: 'pointer', marginTop: 8 },
  addrEditBox:  { marginTop: 12, background: COLORS.white, borderRadius: 16, padding: 16, border: `1px solid ${COLORS.border}` },
  addrSaveBtn:  { padding: '10px 18px', borderRadius: 20, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
  addrCancelBtn:{ padding: '10px 18px', borderRadius: 20, border: `1.5px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
};
